import { motion } from "framer-motion";
import { ArrowUpRight, GraduationCap, HeartPulse, Home } from "lucide-react";
import { EASE, IMAGES } from "../lib/site";

const ImageCard = ({ img, num, title, desc, className }) => (
  <motion.article
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, ease: EASE }}
    className={`group relative overflow-hidden rounded-[28px] ${className}`}
    data-testid={`program-card-${num}`}
  >
    <img
      src={img}
      alt={title}
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-7 sm:p-8">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FAF7F2]/70">
        Program {num}
      </p>
      <h3 className="mt-2 font-serif text-2xl font-semibold text-[#FAF7F2] sm:text-3xl">
        {title}
      </h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-[#FAF7F2]/80">{desc}</p>
    </div>
  </motion.article>
);

const TextCard = ({ num, title, desc, icon: Icon, className }) => (
  <motion.article
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, ease: EASE }}
    className={`group flex flex-col justify-between rounded-[28px] border border-line bg-cardx p-7 transition-colors hover:border-brand sm:p-8 ${className}`}
    data-testid={`program-card-${num}`}
  >
    <div className="flex items-start justify-between">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-[#FDFBF7]">
        <Icon className="h-5 w-5" />
      </span>
      <ArrowUpRight className="h-5 w-5 text-inksoft opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand group-hover:opacity-100" />
    </div>
    <div className="mt-10">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-inksoft">
        Program {num}
      </p>
      <h3 className="mt-2 font-serif text-2xl font-semibold text-ink">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-inksoft">{desc}</p>
    </div>
  </motion.article>
);

export const Programs = () => (
  <section id="programs" data-testid="programs-section" className="py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="eyebrow">Flagship programs</p>
          <h2 className="mt-5 max-w-xl font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">
            Four fronts in the fight <em className="text-brand">against poverty.</em>
          </h2>
        </motion.div>
        <p className="max-w-sm text-sm leading-relaxed text-inksoft">
          Each program pairs immediate relief with a path out — because a meal
          today should become stability tomorrow.
        </p>
      </div>

      <div className="mt-14 grid gap-5 md:grid-cols-6">
        <ImageCard
          className="min-h-[380px] md:col-span-4"
          img={IMAGES.foodRelief}
          num="01"
          title="Emergency Food Relief"
          desc="Community kitchens, mobile pantries and grocery stipends for families across New Jersey and partner regions."
        />
        <TextCard
          className="min-h-[380px] md:col-span-2"
          icon={Home}
          num="02"
          title="Family Housing Stability"
          desc="Rental arrears, utility rescue and transitional housing that keep families in their homes."
        />
        <TextCard
          className="min-h-[380px] md:col-span-2"
          icon={HeartPulse}
          num="04"
          title="Urgent Healthcare Access"
          desc="Clinic vouchers, medicine funds and mobile health days for the uninsured."
        />
        <ImageCard
          className="min-h-[380px] md:col-span-4"
          img={IMAGES.education}
          num="03"
          title="Global Child Education"
          desc="School sponsorships, learning centers and supply drives giving children a classroom and a future."
        />
      </div>
    </div>
  </section>
);
